import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { Text } from '@cbhq/cds-web/typography/Text';
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';
import { defineMessages, useIntl } from '@cbhq/i18n';
import { useGetWorkspaceAuditEventOptions } from '@cbhq/identity-api-hooks/account-management/fetch';

import { InAppErrorMessageFallback } from '../../../components/ErrorBoundary/InAppErrorMessageFallback';
import { teamAccessMessages } from '../../../components/ErrorBoundary/messages';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useScopedOrganizationId } from '../../../hooks/useScopedOrganizationId';
import { makeChipFilterChange } from '../../../utils/makeChipFilterChange';
import type { ActivityFilterOption } from '../components/ActivityFilters';
import { ActivityFilters } from '../components/ActivityFilters';
import { ActivityTable } from '../components/ActivityTable';
import { AuditDetailTray } from '../components/AuditDetailTray';
import { AuditExportPopover } from '../components/AuditExportPopover';
import type { AuditExportInitialValues, AuditExportParams } from '../components/AuditExportTray';
import { AuditExportTray } from '../components/AuditExportTray';
import { useAuditExport } from '../hooks/useAuditExport';
import { usePaginatedAuditEvents } from '../hooks/usePaginatedAuditEvents';
import { getAuditEventTypeOptions } from '../utils/auditLogDisplay';
import { ACTIVITY_DATE_FILTER_OPTIONS } from '../utils/constants';
import { getStartTimeFromDatePreset } from '../utils/helpers';

const messages = defineMessages({
  title: {
    id: 'Activity.home.title',
    defaultMessage: 'Activity',
    description: 'Title for the workspace Activity page',
  },
  noOrgSelectedTitle: {
    id: 'Activity.home.noOrgSelectedTitle',
    defaultMessage: 'No organization selected',
    description: 'Title shown when no organization is selected on the Activity page',
  },
  noOrgSelectedDescription: {
    id: 'Activity.home.noOrgSelectedDescription',
    defaultMessage: 'Select an organization to view activity.',
    description: 'Description shown when no organization is selected on the Activity page',
  },
  errorTitle: {
    id: 'Activity.home.errorTitle',
    defaultMessage: 'Unable to load activity',
    description: 'Title shown when the activity audit log fails to load',
  },
  errorDescription: {
    id: 'Activity.home.errorDescription',
    defaultMessage: 'Something went wrong while loading activity. Please try again later.',
    description: 'Description shown when the activity audit log fails to load',
  },
});

const DATE_FILTER_MESSAGES = defineMessages({
  'last-24-hours': {
    id: 'Activity.home.dateFilter.last24Hours',
    defaultMessage: 'Last 24 hours',
    description: 'Date filter option for audit events from the last 24 hours',
  },
  'last-7-days': {
    id: 'Activity.home.dateFilter.last7Days',
    defaultMessage: 'Last 7 days',
    description: 'Date filter option for audit events from the last 7 days',
  },
  'last-30-days': {
    id: 'Activity.home.dateFilter.last30Days',
    defaultMessage: 'Last 30 days',
    description: 'Date filter option for audit events from the last 30 days',
  },
});

export function ActivityPage() {
  const { formatMessage } = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedAuditEventId, setSelectedAuditEventId] = useState<string | null>(null);
  const [isTrayLoadingNext, setIsTrayLoadingNext] = useState(false);
  const pendingNextAuditLogIndexRef = useRef<number | null>(null);
  const { hasRequestedOrganizationAccessDenied } = useOrganization();
  const organizationId = useScopedOrganizationId();
  const actorIdFromSearchParams = searchParams.get('actorId');

  const [actorFilter, setActorFilter] = useState<string[]>(
    actorIdFromSearchParams ? [actorIdFromSearchParams] : [],
  );
  const [actionTypeFilter, setActionTypeFilter] = useState<string[]>([]);
  const [dateFilter, setDateFilter] = useState<string | null>(null);
  const [emailFilter, setEmailFilter] = useState('');
  const [isExportTrayOpen, setIsExportTrayOpen] = useState(false);
  const [exportTrayInitialValues, setExportTrayInitialValues] = useState<
    AuditExportInitialValues | undefined
  >(undefined);

  const adaptedSetActionTypeFilter = useMemo(() => makeChipFilterChange(setActionTypeFilter), []);

  useEffect(() => {
    setActorFilter(actorIdFromSearchParams ? [actorIdFromSearchParams] : []);
  }, [actorIdFromSearchParams]);

  const auditEventOptions = useGetWorkspaceAuditEventOptions(
    { organizationId },
    { skip: organizationId.length === 0 },
  );

  const auditEventFilters = useMemo(
    () => ({
      actorIds: actorFilter,
      eventTypes: actionTypeFilter,
      startTime: getStartTimeFromDatePreset(dateFilter),
      actorEmail: emailFilter,
    }),
    [actorFilter, actionTypeFilter, dateFilter, emailFilter],
  );

  const { auditLogs, isInitialLoading, isLoadingMore, hasMore, error, loadMore } =
    usePaginatedAuditEvents(organizationId, auditEventFilters);

  const { exportLast90Days, exportCustomReport, isExporting } = useAuditExport(organizationId);

  useEffect(() => {
    logEvent('activity_page_viewed', {
      componentType: ComponentType.page,
      action: ActionType.view,
    });
  }, []);

  // The actor and email filter options, and each email's actor ID (the first actor
  // listed with that email), from one pass over the actors.
  const { actorOptions, emailOptions, actorIdByEmail } = useMemo(() => {
    const actorOptionList: ActivityFilterOption[] = [];
    const idByEmail = new Map<string, string | undefined>();

    for (const actor of auditEventOptions?.actors ?? []) {
      if (actor.id) {
        actorOptionList.push({ value: actor.id, label: actor.name || actor.email || actor.id });
      }
      if (actor.email && !idByEmail.has(actor.email)) {
        idByEmail.set(actor.email, actor.id);
      }
    }

    const byLabel = (a: ActivityFilterOption, b: ActivityFilterOption) =>
      a.label.localeCompare(b.label);

    return {
      actorOptions: actorOptionList.sort(byLabel),
      emailOptions: [...idByEmail.keys()]
        .map((email) => ({ value: email, label: email }))
        .sort(byLabel),
      actorIdByEmail: idByEmail,
    };
  }, [auditEventOptions?.actors]);

  const actionTypeOptions = useMemo(() => getAuditEventTypeOptions(formatMessage), [formatMessage]);

  const dateOptions = useMemo(
    () =>
      ACTIVITY_DATE_FILTER_OPTIONS.map(({ value }) => ({
        value,
        label: formatMessage(DATE_FILTER_MESSAGES[value]),
      })),
    [formatMessage],
  );

  // Derive the selected row from the loaded audit logs so pagination changes stay in sync.
  const selectedAuditLogIndex = selectedAuditEventId
    ? auditLogs.findIndex((row) => row.eventId === selectedAuditEventId)
    : -1;
  const selectedAuditLog =
    selectedAuditLogIndex >= 0 ? auditLogs[selectedAuditLogIndex] : undefined;
  const selectedAuditLogPosition =
    selectedAuditLogIndex >= 0 ? selectedAuditLogIndex + 1 : undefined;

  // The tray treats omitted navigation handlers as disabled controls.
  const canNavigatePrevious = selectedAuditLogIndex > 0;
  const canNavigateNext =
    selectedAuditLogIndex >= 0 && (selectedAuditLogIndex < auditLogs.length - 1 || hasMore);

  const resetTraySelection = useCallback(() => {
    pendingNextAuditLogIndexRef.current = null;
    setIsTrayLoadingNext(false);
    setSelectedAuditEventId(null);
  }, []);

  const handleOpenExportTray = useCallback(() => {
    setExportTrayInitialValues(undefined);
    setIsExportTrayOpen(true);
  }, []);
  const handleCloseExportTray = useCallback(() => {
    setIsExportTrayOpen(false);
  }, []);
  const handleExportAuditLog = useCallback(
    (params: AuditExportParams) =>
      exportCustomReport({
        actorIds: params.actors,
        eventTypes: params.actionTypes,
        // Omit empty date bounds so the API treats them as unset rather than
        // receiving an empty string, which fails ISO timestamp validation.
        startTime: params.startTime || undefined,
        endTime: params.endTime || undefined,
      }),
    [exportCustomReport],
  );
  // "This view" snapshots the live page filters into the tray at click time.
  // The page filters by email through the server-resolved actorEmail param, but the
  // export tray has no email field. Resolve the email to its actor id (the same
  // resolution the backend does before merging into actor_ids) and fold it into the
  // actor filter so the export matches the view the user is looking at.
  const handleSelectThisViewExport = useCallback(() => {
    const actorIdForEmail = emailFilter ? actorIdByEmail.get(emailFilter) : undefined;
    const actors =
      actorIdForEmail && !actorFilter.includes(actorIdForEmail)
        ? [...actorFilter, actorIdForEmail]
        : actorFilter;

    setExportTrayInitialValues({
      actors,
      actionTypes: actionTypeFilter,
      startTime: dateFilter ? getStartTimeFromDatePreset(dateFilter) : undefined,
      endTime: dateFilter ? new Date().toISOString() : undefined,
    });
    setIsExportTrayOpen(true);
  }, [actorFilter, actionTypeFilter, actorIdByEmail, dateFilter, emailFilter]);

  const handleAuditLogClick = useCallback((eventId: string) => {
    logEvent('activity_audit_log_clicked', {
      componentType: ComponentType.table,
      action: ActionType.click,
    });
    pendingNextAuditLogIndexRef.current = null;
    setIsTrayLoadingNext(false);
    setSelectedAuditEventId(eventId);
  }, []);
  const handleCloseAuditDetailTray = useCallback(() => {
    resetTraySelection();
  }, [resetTraySelection]);
  const handleEmailFilterChange = useCallback(
    (value: string) => {
      logEvent('activity_search_used', {
        componentType: ComponentType.search_bar,
        action: ActionType.search,
      });
      resetTraySelection();
      setEmailFilter(value);
    },
    [resetTraySelection],
  );
  const handleActionTypeFilterChange = useCallback(
    (value: string | string[] | null) => {
      logEvent('activity_filter_changed', {
        componentType: ComponentType.dropdown,
        action: ActionType.select,
        filter_type: 'action_type',
      });
      resetTraySelection();
      adaptedSetActionTypeFilter(value);
    },
    [adaptedSetActionTypeFilter, resetTraySelection],
  );
  const handleDateFilterChange = useCallback(
    (value: string | null) => {
      logEvent('activity_filter_changed', {
        componentType: ComponentType.dropdown,
        action: ActionType.select,
        filter_type: 'date',
      });
      resetTraySelection();
      setDateFilter(value);
    },
    [resetTraySelection],
  );
  const handleClearFilters = useCallback(() => {
    logEvent('activity_filters_cleared', {
      componentType: ComponentType.button,
      action: ActionType.click,
    });
    resetTraySelection();
    if (actorIdFromSearchParams) {
      setSearchParams(
        (currentParams) => {
          const nextParams = new URLSearchParams(currentParams);
          nextParams.delete('actorId');
          return nextParams;
        },
        { replace: true },
      );
    }
    setActorFilter([]);
    setActionTypeFilter([]);
    setDateFilter(null);
    setEmailFilter('');
  }, [actorIdFromSearchParams, resetTraySelection, setSearchParams]);
  const handlePreviousAuditLog = useCallback(() => {
    if (selectedAuditLogIndex <= 0) {
      return;
    }

    logEvent('activity_detail_tray_navigate', {
      componentType: ComponentType.button,
      action: ActionType.click,
      direction: 'previous',
    });

    if (isTrayLoadingNext) {
      pendingNextAuditLogIndexRef.current = null;
      setIsTrayLoadingNext(false);
      return;
    }

    pendingNextAuditLogIndexRef.current = null;
    setIsTrayLoadingNext(false);
    setSelectedAuditEventId(auditLogs[selectedAuditLogIndex - 1].eventId);
  }, [auditLogs, selectedAuditLogIndex, isTrayLoadingNext]);
  const handleNextAuditLog = useCallback(() => {
    if (selectedAuditLogIndex < 0) {
      return;
    }

    logEvent('activity_detail_tray_navigate', {
      componentType: ComponentType.button,
      action: ActionType.click,
      direction: 'next',
    });

    const nextAuditLog = auditLogs[selectedAuditLogIndex + 1];
    if (nextAuditLog) {
      setSelectedAuditEventId(nextAuditLog.eventId);
      return;
    }

    if (hasMore) {
      pendingNextAuditLogIndexRef.current = selectedAuditLogIndex + 1;
      setIsTrayLoadingNext(true);
      loadMore();
    }
  }, [auditLogs, hasMore, loadMore, selectedAuditLogIndex]);

  // When Next loads another page, select the first newly loaded row once it arrives.
  useEffect(() => {
    const pendingNextAuditLogIndex = pendingNextAuditLogIndexRef.current;
    if (pendingNextAuditLogIndex == null) {
      return;
    }

    const nextAuditLog = auditLogs[pendingNextAuditLogIndex];
    if (!nextAuditLog) {
      return;
    }

    pendingNextAuditLogIndexRef.current = null;
    setIsTrayLoadingNext(false);
    setSelectedAuditEventId(nextAuditLog.eventId);
  }, [auditLogs]);

  useEffect(() => {
    if (!error || isLoadingMore || !isTrayLoadingNext) {
      return;
    }

    pendingNextAuditLogIndexRef.current = null;
    setIsTrayLoadingNext(false);
  }, [error, isLoadingMore, isTrayLoadingNext]);

  if (hasRequestedOrganizationAccessDenied) {
    return (
      <InAppErrorMessageFallback
        title={formatMessage(teamAccessMessages.deniedTitle)}
        description={formatMessage(teamAccessMessages.deniedDescription)}
      />
    );
  }

  if (!organizationId) {
    return (
      <InAppErrorMessageFallback
        title={formatMessage(messages.noOrgSelectedTitle)}
        description={formatMessage(messages.noOrgSelectedDescription)}
      />
    );
  }

  if (error && auditLogs.length === 0) {
    return (
      <InAppErrorMessageFallback
        title={formatMessage(messages.errorTitle)}
        description={formatMessage(messages.errorDescription)}
      />
    );
  }

  return (
    <VStack
      className="workspaceContentFadeIn"
      gap={4}
      width="100%"
      data-testid="activity-home-page"
    >
      <Text as="h1" font="title1">
        {formatMessage(messages.title)}
      </Text>

      <HStack width="100%" alignItems="center" justifyContent="space-between" gap={2}>
        <ActivityFilters
          emailFilter={emailFilter}
          onEmailFilterChange={handleEmailFilterChange}
          emailOptions={emailOptions}
          hasActorFilter={actorFilter.length > 0}
          actionTypeFilter={actionTypeFilter}
          onActionTypeFilterChange={handleActionTypeFilterChange}
          actionTypeOptions={actionTypeOptions}
          dateFilter={dateFilter}
          onDateFilterChange={handleDateFilterChange}
          dateOptions={dateOptions}
          onClearFilters={handleClearFilters}
        />
        <AuditExportPopover
          last90DaysActionCount={auditEventOptions?.last90DaysCount}
          isExporting={isExporting}
          onSelectLast90Days={exportLast90Days}
          onSelectThisView={handleSelectThisViewExport}
          onSelectCustomReport={handleOpenExportTray}
        />
      </HStack>

      <ActivityTable
        auditLogs={auditLogs}
        isLoadingMore={isInitialLoading || isLoadingMore}
        hasMore={hasMore}
        onLoadMore={loadMore}
        onAuditLogClick={handleAuditLogClick}
      />
      {selectedAuditLog ? (
        <AuditDetailTray
          auditLog={selectedAuditLog}
          onClose={handleCloseAuditDetailTray}
          currentPosition={selectedAuditLogPosition}
          totalCount={auditLogs.length}
          onPrevious={canNavigatePrevious ? handlePreviousAuditLog : undefined}
          onNext={canNavigateNext ? handleNextAuditLog : undefined}
          isLoading={isTrayLoadingNext}
        />
      ) : null}
      {isExportTrayOpen ? (
        <AuditExportTray
          onClose={handleCloseExportTray}
          onExport={handleExportAuditLog}
          isExporting={isExporting}
          actionTypeOptions={actionTypeOptions}
          actorOptions={actorOptions}
          initialValues={exportTrayInitialValues}
        />
      ) : null}
    </VStack>
  );
}
